import { z } from 'zod'

import { knownWord, lenientList, moneySchema } from '@/schemas/common'
import { isoDateSchema } from '@/schemas/portfolio'
import { SIGNAL_SIDES } from '@/schemas/signals'

/**
 * `/api/v5/signals/chart-overlays/`: what the dashboard chart's four layer
 * chips draw for one symbol, parsed straight off the wire into app shapes as
 * `schemas/signals.ts` does (and for its reason: the tolerance acts on the
 * wire row). Field names are mm-be's (`api/v5/signals/serializers.py`,
 * origin/dev dc6fb5c).
 *
 * Tolerance, per the wire-schema rule:
 *  - each list goes through `lenientList`, so one bad level, marker or
 *    pattern day is dropped and logged, never the whole read;
 *  - a level is only its line (`price`) and its rank: the zone's band, pivot
 *    and touches are sent but nothing draws them yet;
 *  - a marker's `price` is nullable on the wire and stays so: a marker with
 *    no price has no place on the chart, which the drawing decides;
 *  - a pattern's `direction` is a free CharField, so a word this build does
 *    not know is read as neutral rather than failing its day.
 *
 * Levels are the CURRENT ones (they describe today's chart, whatever the
 * window); markers and patterns lie inside [`start`, `end`], the last
 * `sessions` sessions the symbol traded. A listed symbol with no prices has
 * empty lists and a null window.
 */

export const PATTERN_DIRECTIONS = ['bullish', 'bearish', 'neutral'] as const
export type PatternDirection = (typeof PATTERN_DIRECTIONS)[number]

/** One support or resistance zone, drawn as one line at `price`. Rank 1 is nearest the price, 2 the most touched. */
const chartLevelSchema = z.object({
  rank: z.number().int(),
  price: moneySchema,
})
export type ChartLevel = z.infer<typeof chartLevelSchema>

/** A green or red signal on its day, at the price it was taken at. */
const chartMarkerSchema = z.object({
  date: isoDateSchema,
  signal: z.enum(SIGNAL_SIDES),
  price: moneySchema.nullable(),
})
export type ChartMarker = z.infer<typeof chartMarkerSchema>

const candlePatternSchema = z
  .object({
    name: z.string(),
    label: z.string().min(1),
    direction: z.string(),
  })
  .transform(({ name, label, direction }) => ({
    name,
    label,
    direction: knownWord(PATTERN_DIRECTIONS, direction) ?? 'neutral',
  }))
export type CandlePattern = z.infer<typeof candlePatternSchema>

/** The patterns that fired on one session; mm-be sends only days with at least one. */
const patternDaySchema = z.object({
  date: isoDateSchema,
  patterns: lenientList(candlePatternSchema, 'candle pattern'),
})
export type PatternDay = z.infer<typeof patternDaySchema>

export const chartOverlaysSchema = z
  .object({
    symbol: z.string().min(1),
    start: isoDateSchema.nullable(),
    end: isoDateSchema.nullable(),
    levels_date: isoDateSchema.nullable(),
    support: lenientList(chartLevelSchema, 'support level'),
    resistance: lenientList(chartLevelSchema, 'resistance level'),
    markers: lenientList(chartMarkerSchema, 'chart marker'),
    patterns: lenientList(patternDaySchema, 'pattern day'),
  })
  .transform(({ levels_date, ...rest }) => ({ ...rest, levelsDate: levels_date }))
export type ChartOverlays = z.infer<typeof chartOverlaysSchema>
